/**
 * Hook React Query pour les campagnes
 * Avec support Realtime optionnel
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useOrganizationFeatures } from '@/hooks/useOrganizationFeatures'
import { isFeatureEnabled } from '@/config/features'
import { useSecureRealtime } from '@/hooks/useSecureRealtime'
import { healthMonitor } from '@/lib/healthCheck'

interface Referent {
  id: string
  refused_at: string | null
  status: string
}

interface Campaign {
  id: string
  candidate_name: string
  candidate_email: string
  target_position: string
  status: string
  is_draft: boolean | null
  created_at: string | null
  updated_at: string | null
  current_step: number
  recruiter_id: string | null
  ai_extracted_data?: any
  referents_requested_count?: number | null
  referents?: Referent[]
}

interface UseCampaignsQueryOptions {
  includeCompleted?: boolean
}

/**
 * Hook pour charger les campagnes avec React Query + Realtime
 * Adapté au nouveau modèle d'accès par rôle (member/admin/owner)
 */
export function useCampaignsQuery({ 
  includeCompleted = false 
}: UseCampaignsQueryOptions = {}) {
  const { user, profile } = useAuth()
  const { organizationData } = useOrganizationFeatures()
  
  // ✅ Extraire uniquement les valeurs primitives pour éviter les re-renders
  const userRole = organizationData?.userRole
  const orgId = profile?.organization_id
  
  // ✅ PHASE 1B: Gating complet - attendre userId + orgId + userRole
  const isReady = !!user?.id && !!orgId && !!userRole
  
  // ✅ Stabiliser queryKey avec useMemo pour éviter remounts
  const queryKey = useMemo(
    () => ['campaigns', user?.id ?? '', orgId ?? '', userRole ?? '', String(includeCompleted)],
    [user?.id, orgId, userRole, includeCompleted]
  )
  
  // Query React Query avec fallback polling
  const query = useQuery({
    queryKey,
    
    queryFn: async (): Promise<Campaign[]> => {
      if (!user?.id) {
        throw new Error('User not authenticated')
      }

      if (!orgId) {
        throw new Error('User not associated with an organization')
      }

      // Enregistrer le render pour monitoring
      healthMonitor.recordRender()

      let query = supabase
        .from('campaigns')
        .select(`
          *,
          referents (
            id,
            refused_at,
            status
          )
        `)
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false })

      // Filtre selon le rôle
      // member: voit uniquement ses campagnes
      // admin/owner: voient toutes les campagnes de l'org (RLS gère déjà)
      if (userRole === 'member') {
        query = query.eq('recruiter_id', user.id)
      }

      if (!includeCompleted) {
        query = query.neq('status', 'Terminé')
      }

      const { data, error } = await query

      if (error) throw error
      
      // ✅ Normaliser current_step pour garantir le type
      return (data || []).map(campaign => ({
        ...campaign,
        current_step: campaign.current_step ?? 1
      }))
    },
    
    // ✅ PHASE 1B: Attendre que userRole soit défini avant d'activer la query
    enabled: isReady,
    
    // Refetch automatique (contrôlé par feature flag)
    refetchOnWindowFocus: isFeatureEnabled('REACT_QUERY_REFETCH', user?.id),
    refetchOnReconnect: isFeatureEnabled('REACT_QUERY_REFETCH', user?.id),
    
    // Polling fallback (réduit à 10 minutes avec Realtime actif)
    refetchInterval: 10 * 60 * 1000, // 10 minutes (filet de sécurité si Realtime échoue)
    
    // Cache
    staleTime: 10 * 60 * 1000, // 10 minutes (aligné avec refetchInterval)
  })
  
  // Realtime optionnel (avec garde-fous)
  // ✅ Stabiliser realtimeFilter avec useMemo pour éviter remounts
  const realtimeFilter = useMemo(
    () => userRole === 'member' 
      ? `recruiter_id=eq.${user?.id}`
      : `organization_id=eq.${orgId}`,
    [userRole, user?.id, orgId]
  )
  
  useSecureRealtime({
    queryKey,
    table: 'campaigns',
    filter: realtimeFilter,
    userId: user?.id,
    // ✅ PHASE 1B: Utiliser isReady pour cohérence avec useQuery
    enabled: isReady,
  })
  
  // ✅ Retourner isPending et isFetched pour gérer l'empty state proprement
  return {
    ...query,
    // isPending couvre "pas encore démarré" + "en cours sans cache"
    // isFetched garantit qu'on a fetch au moins une fois
  }
}
